import { Label, PreAnnotateSettingsDocument } from "@repo/schema";
import { useEffect, useMemo, useReducer, useRef } from "react";
import {
  initialPreAnnotateFormState,
  isPreAnnotateFormDirty,
  preAnnotateSaveGate,
  preAnnotateSettingsFormReducer,
  toUpdateDocument,
} from "./preAnnotateSettingsForm";

export interface UsePreAnnotateSettingsFormArgs {
  open: boolean;
  settings: PreAnnotateSettingsDocument | undefined;
  projectLabels: readonly Label[];
  /** Saving or still loading — blocks Save without a reason. */
  busy: boolean;
}

/**
 * `useReducer` around the pure form model plus the memoised derivations the
 * dialog renders from: the PUT body and the Save gate.
 */
export const usePreAnnotateSettingsForm = ({
  open,
  settings,
  projectLabels,
  busy,
}: UsePreAnnotateSettingsFormArgs) => {
  const [state, dispatch] = useReducer(
    preAnnotateSettingsFormReducer,
    null,
    initialPreAnnotateFormState,
  );

  // Seed on open (overwriting whatever Cancel left), then follow the document
  // while open. The reducer ignores the follow-ups once the form is dirty, so
  // a label-tag refetch cannot clobber an edit in progress.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    const force = !wasOpenRef.current;
    wasOpenRef.current = true;
    dispatch({ type: "init", settings, force });
  }, [open, settings]);

  const isDirty = useMemo(() => isPreAnnotateFormDirty(state), [state]);
  const document = useMemo(
    () => toUpdateDocument(state, projectLabels),
    [state, projectLabels],
  );
  const saveGate = useMemo(
    () => preAnnotateSaveGate(state, settings?.availability, busy),
    [state, settings?.availability, busy],
  );

  return {
    state,
    dispatch,
    isDirty,
    document,
    saveGate,
  };
};
